import React from "react";
import { EntityEditProvider } from "@/contexts/EntityEditContext";
import type { EntityEditProviderProps } from "@/types/contexts/entityEdit";
import { ProjectType } from "@/types/db/projects";
import { projectService } from "@/services/projectService";

interface ProjectEditProviderProps extends Omit<EntityEditProviderProps<ProjectType>, "onSave" | "entityName"> {
  children: React.ReactNode;
}

export function ProjectEditProvider({ children, entityData, loading, onSaveSuccess }: ProjectEditProviderProps) {
  const handleSave = async (formData: Partial<ProjectType>): Promise<ProjectType> => {
    if (!entityData?.id) {
      throw new Error("No project ID available");
    }

    // Extract relational data that needs to be saved to separate tables
    const {
      id,
      created_at,
      client,
      start_time,
      end_time,
      shifts,
      location,
      dress_code,
      project_abilities,
      project_restrictions,
      ...cleanFormData
    } = formData;

    // Update the base projects table (only direct columns)
    const updatedProject = await projectService.updateProject(entityData.id, cleanFormData);

    // Update link tables if they were modified
    if (project_abilities !== undefined && entityData.client_id && entityData.tenant_id) {
      await projectService.syncProjectAbilities(
        entityData.id,
        entityData.client_id,
        entityData.tenant_id,
        project_abilities || [],
      );
    }

    if (project_restrictions !== undefined && entityData.client_id && entityData.tenant_id) {
      await projectService.syncProjectRestrictions(
        entityData.id,
        entityData.client_id,
        entityData.tenant_id,
        project_restrictions || [],
      );
    }

    // Fetch the complete project data with all joins to return
    const completeProject = await projectService.getProjectById(entityData.id);
    if (!completeProject) {
      throw new Error("Failed to fetch updated project data");
    }

    return completeProject;
  };

  return (
    <EntityEditProvider
      entityData={entityData}
      loading={loading}
      onSave={handleSave}
      onSaveSuccess={onSaveSuccess}
      entityName="Project"
    >
      {children}
    </EntityEditProvider>
  );
}
